// Rasterizes a Design page (the same percentage-based elements
// `DesignCanvas.jsx` renders as DOM boxes) into a real flattened image,
// using only the browser's native Canvas 2D API — no new dependency, no
// server-side render engine. This is what lets "Save Thumbnail" upload a
// real file to the EXISTING `/api/admin/content-sources/{id}/thumbnail`
// endpoint instead of only keeping the design as local state.
//
// Text size is approximated: editor font sizes are relative to the
// on-screen preview box (~420px tall), so they're scaled up to whatever
// pixel height we're rendering at. This is a Level 1/2 approximation,
// not pixel-perfect typography.
const EDITOR_CANVAS_REF_HEIGHT = 420;
const BASE_WIDTH = 1280;

function dimsForAspect(aspectRatio) {
  const [w, h] = (aspectRatio || '16:9').split(':').map(Number);
  return { width: BASE_WIDTH, height: Math.round(BASE_WIDTH * (h / w)) };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image-load-failed'));
    img.src = src;
  });
}

/** Renders one page to a Blob. Rejects if an image element can't be loaded/drawn back out — e.g. a cross-origin frame without CORS headers — rather than silently producing a blank result. */
export async function renderPageToBlob(page, aspectRatio, { mimeType = 'image/jpeg', quality = 0.92 } = {}) {
  const { width, height } = dimsForAspect(aspectRatio);
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = page?.background || '#ffffff';
  ctx.fillRect(0, 0, width, height);
  const scale = height / EDITOR_CANVAS_REF_HEIGHT;

  for (const el of (page?.elements || [])) {
    if (el.visible === false) continue;
    const x = (el.x / 100) * width, y = (el.y / 100) * height;
    const w = (el.w / 100) * width, h = (el.h / 100) * height;

    if (el.type === 'shape') {
      ctx.fillStyle = el.fill || '#3A7BFF';
      if (el.shapeType === 'circle') { ctx.beginPath(); ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2); ctx.fill(); }
      else if (el.shapeType === 'line') { ctx.fillRect(x, y + h / 2 - 2, w, 4); }
      else { ctx.fillRect(x, y, w, h); }
    } else if (el.type === 'image' && el.src) {
      try {
        const img = await loadImage(el.src);
        const elAspect = w / h, imgAspect = img.width / img.height;
        let sx = 0, sy = 0, sw = img.width, sh = img.height;
        if ((el.objectFit || 'cover') === 'cover') {
          if (imgAspect > elAspect) { sw = img.height * elAspect; sx = (img.width - sw) / 2; }
          else { sh = img.width / elAspect; sy = (img.height - sh) / 2; }
        }
        ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
      } catch {
        console.warn('thumbnail render: could not load image element, drawing a placeholder instead', el.id);
        ctx.fillStyle = '#D9DEE6'; ctx.fillRect(x, y, w, h);
      }
    } else if (el.type === 'text') {
      ctx.fillStyle = el.color || '#111111';
      ctx.font = `${el.bold ? 700 : 400} ${Math.max(8, Math.round((el.fontSize || 24) * scale))}px sans-serif`;
      ctx.textBaseline = 'middle';
      const align = el.align || 'left';
      ctx.textAlign = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';
      const tx = align === 'center' ? x + w / 2 : align === 'right' ? x + w : x;
      ctx.fillText(el.text || 'Text', tx, y + h / 2, w);
    }
  }

  // Reading pixels back out of a canvas that drew a cross-origin, non-CORS
  // image throws here (tainted canvas) — caught by the caller.
  return new Promise((resolve, reject) => {
    try { canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob-failed'))), mimeType, quality); }
    catch (err) { reject(err); }
  });
}
